import React from 'react';
import {
  JOB_SOURCE_ICON,
  type ApprovalSummary,
  type BuildCheck,
  type BuildSummary,
  type ConfirmedStatus,
  type JobRequest,
  type JobRisk,
  type JobSource,
  type ProposedAction,
  type SpecSummary,
} from '../../../lib/aiJobSummary';
import { Badge, Icon } from '../../ui';
import { LinkButton } from './SectionHeading';

/**
 * Requests grouped by source. Each request is a row that fills in over the
 * life of the job: asked → proposed → built → live. Unfilled stages show a
 * faint dashed placeholder so the shape of the page never changes between
 * stages; only the ink does.
 */

const KIND_TONE: Record<NonNullable<JobRequest['kind']>, 'accent' | 'info' | 'warn' | 'neutral'> = {
  build: 'accent',
  question: 'info',
  bug: 'warn',
  answer: 'neutral',
};

const RISK_LABEL: Record<JobRisk, string> = {
  email: 'sends email',
  payments: 'payments',
  auth: 'logins',
  'prod-data': 'live data',
  sms: 'sends sms',
};

const LIVE_TONE: Record<ConfirmedStatus, 'up' | 'warn' | 'down'> = {
  live: 'up',
  partial: 'warn',
  'not-live': 'down',
};

const LIVE_LABEL: Record<ConfirmedStatus, string> = {
  live: 'Live',
  partial: 'Partly live',
  'not-live': 'Not live',
};

type Group = { source: JobSource; requests: JobRequest[] };

function groupBySource(spec: SpecSummary): Group[] {
  const byId = new Map(spec.sources.map((s) => [s.id, s]));
  const groups: Group[] = [];
  const index = new Map<string, Group>();
  for (const req of spec.requests) {
    const source = byId.get(req.source) ?? { id: req.source, kind: 'other' as const, label: req.source };
    let group = index.get(source.id);
    if (!group) {
      group = { source, requests: [] };
      index.set(source.id, group);
      groups.push(group);
    }
    group.requests.push(req);
  }
  return groups;
}

export default function RequestTimeline({
  spec,
  build,
  approval,
  changed,
}: {
  spec: SpecSummary;
  build?: BuildSummary;
  approval?: ApprovalSummary;
  /** Request ids whose row changed since the previous version of the current stage. */
  changed?: Set<string>;
}) {
  const groups = groupBySource(spec);
  const actionFor = new Map(spec.actions.map((a) => [a.request, a]));
  const checkFor = new Map((build?.checks ?? []).map((c) => [c.request, c]));
  const confirmedFor = new Map((approval?.confirmed ?? []).map((c) => [c.request, c]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
      {groups.map((group) => (
        <div key={group.source.id} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SourceHeader source={group.source} count={group.requests.length} />
          {group.requests.map((req) => (
            <RequestRow
              key={req.id}
              request={req}
              action={actionFor.get(req.id)}
              check={checkFor.get(req.id)}
              confirmed={confirmedFor.get(req.id)}
              hasBuild={!!build}
              hasApproval={!!approval}
              changed={changed?.has(req.id) ?? false}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function SourceHeader({ source, count }: { source: JobSource; count: number }) {
  const inner = (
    <>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          color: 'var(--text-muted)',
          flexShrink: 0,
        }}
      >
        <Icon name={JOB_SOURCE_ICON[source.kind] ?? 'link'} size={13} />
      </span>
      <span
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
          color: 'var(--text-body)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {source.label}
      </span>
      <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', flexShrink: 0 }}>
        {count} {count === 1 ? 'request' : 'requests'}
      </span>
      {source.url ? <Icon name="external-link" size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} /> : null}
    </>
  );
  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px 4px 4px',
    marginLeft: '-4px',
    borderRadius: 'var(--radius-sm)',
    textDecoration: 'none',
    maxWidth: '100%',
  };
  return source.url ? (
    <a href={source.url} target="_blank" rel="noopener noreferrer" className="hover-wash" style={style}>
      {inner}
    </a>
  ) : (
    <div style={style}>{inner}</div>
  );
}

function RequestRow({
  request,
  action,
  check,
  confirmed,
  hasBuild,
  hasApproval,
  changed = false,
}: {
  request: JobRequest;
  action?: ProposedAction;
  check?: BuildCheck;
  confirmed?: ApprovalSummary['confirmed'][number];
  hasBuild: boolean;
  hasApproval: boolean;
  changed?: boolean;
}) {
  const kind = request.kind;
  const isAnswerOnly = kind === 'question' || kind === 'answer';
  return (
    <article
      className="hover-lift"
      style={{
        position: 'relative',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        padding: '14px 16px 16px',
        overflow: 'hidden',
      }}
    >
      {changed ? (
        <span aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'var(--accent)' }} />
      ) : null}
      <div className="ck-op-req">
        <div
          className="t-num"
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-faint)',
            paddingTop: '3px',
            letterSpacing: '0.02em',
          }}
        >
          {request.id}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <p
              style={{
                margin: 0,
                flex: '1 1 320px',
                minWidth: 0,
                fontSize: 'var(--text-base)',
                lineHeight: 'var(--leading-normal)',
                color: 'var(--text-body)',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              }}
            >
              {request.text}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingTop: '2px' }}>
              {changed ? (
                <Badge tone="accent" dot>
                  changed
                </Badge>
              ) : null}
              {kind ? <Badge tone={KIND_TONE[kind]}>{kind}</Badge> : null}
              {(request.risks ?? []).map((risk) => (
                <span
                  key={risk}
                  title="Touches something with real-world consequences"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    height: '18px',
                    padding: '0 7px',
                    borderRadius: 'var(--radius-pill)',
                    background: 'var(--status-warn-soft)',
                    color: 'var(--status-warn)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '9.5px',
                    // See Badge: uppercase in a normal line box reads high.
                    lineHeight: 1,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon name="triangle-alert" size={10} strokeWidth={2.25} />
                  {RISK_LABEL[risk]}
                </span>
              ))}
            </div>
          </div>

          <div className="ck-op-stages" style={{ marginTop: '12px' }}>
            <StageCell label="Proposed" filled={!!action}>
              {action ? (
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-body)' }}>
                  {action.text}
                </p>
              ) : (
                <Placeholder icon="pencil" text="no proposal yet" />
              )}
            </StageCell>

            <StageCell label="Built" filled={!!check}>
              {check ? (
                <BuiltCell check={check} />
              ) : hasBuild ? (
                <Placeholder
                  icon={isAnswerOnly ? 'message-square' : 'pause-circle'}
                  text={isAnswerOnly ? 'answered, nothing to build' : 'not in this build'}
                />
              ) : (
                <Placeholder icon="hammer" text="waiting for build" />
              )}
            </StageCell>

            <StageCell label="Live" filled={!!confirmed}>
              {confirmed ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                  <Badge tone={LIVE_TONE[confirmed.status]} dot>
                    {LIVE_LABEL[confirmed.status]}
                  </Badge>
                  {confirmed.note ? (
                    <span style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--text-muted)' }}>
                      {confirmed.note}
                    </span>
                  ) : null}
                </div>
              ) : hasApproval ? (
                <Placeholder icon="help-circle" text="not confirmed" />
              ) : (
                <Placeholder icon="rocket" text="not live yet" />
              )}
            </StageCell>
          </div>
        </div>
      </div>
    </article>
  );
}

function StageCell({ label, filled, children }: { label: string; filled: boolean; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span
          aria-hidden
          style={{
            width: '6px',
            height: '6px',
            borderRadius: 'var(--radius-pill)',
            background: filled ? 'var(--accent)' : 'var(--border-strong)',
            flexShrink: 0,
          }}
        />
        <span className="t-eyebrow" style={{ color: filled ? 'var(--text-muted)' : 'var(--text-faint)' }}>
          {label}
        </span>
      </div>
      {filled ? (
        <div
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-md)',
            padding: '10px 12px',
            minHeight: '44px',
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}

function Placeholder({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="ck-op-placeholder">
      <Icon name={icon} size={13} style={{ flexShrink: 0, opacity: 0.8 }} />
      <span>{text}</span>
    </div>
  );
}

function BuiltCell({ check }: { check: BuildCheck }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-faint)', textDecoration: 'line-through', textDecorationColor: 'var(--border-strong)' }}>
          {check.before}
        </span>
        <span style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
          <Icon name="arrow-right" size={13} strokeWidth={2} style={{ color: 'var(--accent-text)', flexShrink: 0, marginTop: '3px' }} />
          <span style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-body)' }}>{check.after}</span>
        </span>
      </div>
      <div>
        <div className="t-eyebrow" style={{ marginBottom: '3px', fontSize: '9.5px' }}>
          How to check
        </div>
        <span style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-snug)', color: 'var(--text-muted)' }}>{check.how_to_check}</span>
      </div>
      {check.url ? (
        <div>
          <LinkButton href={check.url} variant="secondary" icon="eye">
            Open preview
          </LinkButton>
        </div>
      ) : null}
    </div>
  );
}
